import { Injectable } from '@angular/core';
import {
  OrganizationSchema,
  WebsiteSchema,
  BreadcrumbSchema,
  SoftwareApplicationSchema,
  FAQSchema,
  HowToSchema,
} from './schema-data.model';

@Injectable({
  providedIn: 'root',
})
export class SchemaService {
  private baseUrl = 'https://www.thepdfleader.com/';

  /** ✅ Organization Schema */
  getOrganizationSchema(): Omit<OrganizationSchema, '@context'> {
    return {
      "@type": "Organization",
      name: "The PDF Leader",
      url: this.baseUrl,
      logo: `${this.baseUrl}assets/images/logo.png`,
      sameAs: ["https://www.facebook.com/ThePdfLeader"],
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+91-XXXXXXXXXX",
          contactType: "customer support",
          email: "support@thepdfleader.com",
          areaServed: "IN",
        },
      ],
    };
  }

  /** ✅ Website Schema */
  getWebsiteSchema(): Omit<WebsiteSchema, '@context'> {
    return {
      "@type": "WebSite",
      url: this.baseUrl,
      potentialAction: {
        "@type": "SearchAction",
        target: `${this.baseUrl}?s={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
  }

  /** ✅ Breadcrumb Schema */
  getBreadcrumbSchema(url: string): Omit<BreadcrumbSchema, '@context'> {
    const parts = url.replace(this.baseUrl, '').split('/').filter(Boolean);

    const breadcrumbs = parts.map((part, index) => ({
      "@type": "ListItem",
      position: index + 2,
      name: this.getBreadcrumbName(part),
      item: this.baseUrl + parts.slice(0, index + 1).join('/'),
    }));

    return {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: this.baseUrl },
        ...breadcrumbs,
      ],
    };
  }

  /** ✅ SoftwareApplication Schema */
  getSoftwareApplicationSchema(toolName: string, url: string, description: string): SoftwareApplicationSchema {
    const absoluteUrl = url.startsWith('http') ? url : this.baseUrl + url.replace(/^\/+/, '');
    return {
      "@type": "SoftwareApplication",
      name: `${toolName} - The PDF Leader`,
      operatingSystem: "All",
      applicationCategory: "WebApplication",
      url: absoluteUrl,
      description,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      provider: {
        "@type": "Organization",
        name: "The PDF Leader",
        url: this.baseUrl,
      },
    };
  }

  /** ✅ FAQ Schema */
  getFAQSchema(faqs: { q: string; a: string }[]): Omit<FAQSchema, '@context'> {
    return {
      "@type": "FAQPage",
      mainEntity: faqs.map(faq => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.a,
        },
      })),
    };
  }

  /** ✅ HowTo Schema */
  getHowToSchema(name: string, description: string, steps: { title: string; text: string }[]): Omit<HowToSchema, '@context'> {
    return {
      "@type": "HowTo",
      name,
      description,
      step: steps.map(step => ({
        "@type": "HowToStep",
        name: step.title,
        text: step.text,
      })),
    };
  }

  /** ✅ Wrapper to combine schemas under one @context */
  buildSchema(...schemas: any[]): string {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@graph": schemas,
    });
  }

  /** Utility: Capitalize breadcrumb name properly */
  private getBreadcrumbName(text: string): string {
    // Custom mappings for known pages
    const map: Record<string, string> = {
      'pdf-merge': 'Merge PDF',
      'tools': 'Tools',
      'faq': 'FAQ',
      'how-to': 'How To',
    };
    return map[text.toLowerCase()] || this.capitalize(text.replace('-', ' '));
  }

  private capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}
